import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledNameWrapper,
  StyledParameterCard,
  StyledParameterWrapper,
  StyledValueWrapper,
} from './styles';
import IconButton from '__components/Kizen/IconButton';
import { IconSizing } from '__components/Kizen/Icon';
import Icon from '__components/Kizen/Icon';
import TextInput from '__components/Inputs/TextInput';
import { useFlashTransition } from 'hooks/useFlashState';

export type ParameterItem = {
  id: string;
  name: string;
  value: string;
  error?: string;
};

const buttonColor = {
  default: 'var(--color-background-dark)',
  hover: 'var(--color-background-darker)',
};

export const SQLParameter: FC<{
  parameter: ParameterItem;
  onChange: (parameter: ParameterItem, index: number) => void;
  onDelete: (index: number) => void;
  index: number;
  uniqueNames: string[];
}> = ({ parameter, onChange, onDelete, index, uniqueNames }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(parameter.name);
  const [value, setValue] = useState(parameter.value);

  const [message, showMessage, flashError, cancelFlash] = useFlashTransition();

  useEffect(() => {
    if (parameter.error) {
      flashError(parameter.error);
    }
  }, [parameter.error, flashError]);

  const validate = useMemo(
    () => ({ message, showMessage }),
    [message, showMessage]
  );

  const onChangeName = (name: string) => {
    setName(name);
    cancelFlash();
  };

  const onBlurName = () => {
    const trimmedName = name.trim();
    setName(trimmedName);
    onChange(
      {
        ...parameter,
        name: trimmedName,
        error: uniqueNames.some((n, i) => n === trimmedName && i !== index)
          ? t('Parameter name must be unique')
          : !trimmedName && value
            ? t('Parameters must have a name')
            : undefined,
      },
      index
    );
  };

  const onChangeValue = (value: string) => {
    setValue(value);
  };

  const onBlurValue = () => {
    const trimmedValue = value.trim();
    setValue(trimmedValue);
    onChange(
      {
        ...parameter,
        value: trimmedValue,
        error: uniqueNames.some((n, i) => n === name && i !== index)
          ? t('Parameter name must be unique')
          : !name && trimmedValue
            ? t('Parameters must have a name')
            : undefined,
      },
      index
    );
  };

  const handleDelete = () => {
    onDelete(index);
  };

  return (
    <StyledParameterWrapper>
      <IconButton
        title={t('Delete')}
        sizing="dense"
        color={buttonColor}
        onClick={handleDelete}
      >
        <IconSizing size="15px">
          <Icon icon="trash" />
        </IconSizing>
      </IconButton>
      <StyledParameterCard hasError={!!parameter.error}>
        <StyledNameWrapper>
          <TextInput
            label={t('Parameter Name')}
            placeholder={t('Enter Parameter Name')}
            value={name}
            onChange={onChangeName}
            onBlur={onBlurName}
            error={parameter.error}
            validate={validate}
          />
        </StyledNameWrapper>
        <StyledValueWrapper>
          <TextInput
            label={t('Parameter Value')}
            placeholder={t('Enter Value')}
            value={value}
            onChange={onChangeValue}
            onBlur={onBlurValue}
          />
        </StyledValueWrapper>
      </StyledParameterCard>
    </StyledParameterWrapper>
  );
};
